<script setup lang="ts">
/**
 * A payload viewer for run output, gate payloads and tool results.
 *
 * These were `<pre>` blocks of undifferentiated white monospace — technically the data, practically
 * unreadable, and BG §22 asks for "dense but clear". This colours the structure, decodes JSON that
 * arrived wrapped in a string, and lets large branches stay collapsed.
 *
 * Anything that is not JSON — a run log, a stack trace — falls back to plain preformatted text
 * rather than being mangled into a tree.
 */
import { computed } from 'vue'
import { parsePayload } from '../lib/json'
import ZhJsonNode from './ZhJsonNode.vue'

const props = withDefaults(
    defineProps<{
        /** The payload: a JSON string, or an already-parsed value. */
        value: unknown
        /** Depth to leave expanded. Two shows the shape without unrolling a thousand-row result. */
        openDepth?: number
        /** Accessible name for the region, e.g. "Step payload". */
        label?: string
    }>(),
    { openDepth: 3, label: 'Payload' },
)

const parsed = computed(() => parsePayload(props.value))

/*
 * The root never has a key, so giving it its own disclosure row costs a line and shows nothing.
 * Its children are rendered directly; a root primitive still needs the row.
 */
const rootEntries = computed(() => {
    const node = parsed.value
    if (node?.kind === 'object') return node.entries
    if (node?.kind === 'array') return node.items.map((item, index) => ({ key: String(index), node: item }))
    return null
})
const fallback = computed(() => (typeof props.value === 'string' ? props.value : String(props.value ?? '')))
</script>

<template>
    <div class="zh-json" :aria-label="label" role="group">
        <template v-if="rootEntries">
            <ZhJsonNode
                v-for="entry in rootEntries"
                :key="entry.key"
                :node="entry.node"
                :name="entry.key"
                :open-depth="openDepth"
            />
        </template>
        <ZhJsonNode v-else-if="parsed" :node="parsed" :open-depth="openDepth" />
        <pre v-else class="zh-json__raw">{{ fallback }}</pre>
    </div>
</template>

<style scoped>
.zh-json {
    overflow-x: auto;
    background: var(--void-black);
    border: 1px solid var(--border);
    padding: 0.75rem;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    line-height: 1.6;
}

.zh-json__raw {
    margin: 0;
    color: var(--dirty-white);
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
</style>
